/**
 * InterviewRoundCard Component
 *
 * Compact interview round card with must-qualify toggle, delete action, drag handle
 * Clicking on the card opens the editable round modal
 */

import React from "react";
import { Card, Typography, Button, Tooltip, Popconfirm, Tag, Empty, Switch } from "antd";
import {
  DeleteOutlined,
  HolderOutlined,
  EditOutlined,
} from "@ant-design/icons";
import { InterviewRoundState } from "../utils/formTransformers";

interface InterviewRoundCardProps {
  round: InterviewRoundState;
  index: number;
  isProfileMatching: boolean;
  onEdit: (round: InterviewRoundState) => void;
  onDelete: (roundId: string) => void;
  dragHandleProps?: any; // Props for drag handle from @dnd-kit
  isDragging?: boolean;
  isEditMode?: boolean;
  isDragDisabled?: boolean; // True when drag is disabled for existing rounds in edit mode
  isProfileMatchRequired?: boolean;
  onProfileMatchRequiredChange?: (checked: boolean) => void;
  isProfileMatchRequiredDisabled?: boolean;
}

const InterviewRoundCard: React.FC<InterviewRoundCardProps> = ({
  round,
  isProfileMatching,
  onEdit,
  onDelete,
  dragHandleProps,
  isDragging = false,
  isEditMode = false,
  isDragDisabled = false,
  isProfileMatchRequired = true,
  onProfileMatchRequiredChange,
  isProfileMatchRequiredDisabled = false,
}) => {
  const handleDelete = () => {
    const roundId = round.interviewRoundId || round._id || "";
    if (roundId) {
      onDelete(roundId);
    }
  };

  // Prevent drag when interacting with buttons/switches
  const handleInteractiveElementMouseDown = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  // Determine if this is Overall Assessment by ID/title (not by round number, since positions can change)
  const isOverallAssessment =
    round.title?.toLowerCase() === "overall assessment" ||
    round.interviewRoundId === "overall-assessment" ||
    round._id === "overall-assessment";
  // Profile Matching is a fixed round (not draggable) at position 1
  const isDefaultRound = isProfileMatching;

  // Format card title (without Round # prefix, as it's shown in the border)
  let displayTitle = round.title;
  if (!displayTitle || displayTitle.trim() === "") {
    if (isProfileMatching) {
      displayTitle = "Profile Matching";
    } else if (isOverallAssessment) {
      displayTitle = "Overall Assessment";
    } else {
      displayTitle = "Untitled Round";
    }
  }
  const roundTitle = displayTitle;

  // Use questionGenerationPrompt as description if available, otherwise use description
  // Profile Matching is hardcoded (not in DB), so keep fallback for it
  // All other rounds should get descriptions from database only
  let cardDescription = "";
  if (isProfileMatching) {
    cardDescription =
      "This stage is intended to validate the candidate's suitability for the specified job role.";
  } else if (round.answerType === "simulation") {
    cardDescription =
      "AI-powered simulation assessment. Candidates will be evaluated in a realistic, scenario-based environment.";
  } else {
    // For all other rounds, only use data from database - no fallback
    cardDescription = round.questionGenerationPrompt || round.description || "";
  }

  // Determine if card is clickable
  // In edit mode, existing rounds (not newly added) should not be clickable
  const isExistingRoundInEditMode = isEditMode && !round._isNewlyAdded;
  const isCardClickable = !isProfileMatching && !isExistingRoundInEditMode;

  // Handle card click/keyboard to open editable modal
  // Profile Matching cannot be edited at all
  // Overall Assessment can be edited in settings-only mode
  // Existing rounds in edit mode cannot be edited
  const handleCardClick = (e: React.MouseEvent) => {
    // Don't open modal for Profile Matching, existing rounds in edit mode, or if clicking on interactive elements
    if (isProfileMatching || isExistingRoundInEditMode) {
      return;
    }

    const target = e.target as HTMLElement;
    const isInteractiveElement =
      target.closest("button") ||
      target.closest(".ant-switch") ||
      target.closest(".ant-popconfirm") ||
      target.closest(".round-drag-handle") ||
      target.closest("a");

    if (!isInteractiveElement) {
      onEdit(round);
    }
  };

  const handleCardKeyDown = (e: React.KeyboardEvent) => {
    // Don't open modal for Profile Matching or existing rounds in edit mode
    if (isProfileMatching || isExistingRoundInEditMode) {
      return;
    }

    // Open modal on Enter or Space key
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onEdit(round);
    }
  };

  // Determine if drag handle should appear disabled
  const isDragHandleDisabled = isProfileMatching || isDragDisabled;

  return (
    <>
      <Card
        className={`interview-round-card interview-round-card--round-${
          round.interviewRoundNumber
        } ${
          isProfileMatching ? "interview-round-card--profile-matching" : ""
        } ${isDragging ? "interview-round-card--dragging" : ""} ${
          isExistingRoundInEditMode ? "interview-round-card--readonly" : ""
        }`}
        style={{
          marginBottom: 6,
          opacity: isDragging ? 0.5 : 1,
          cursor: isCardClickable ? "pointer" : "default",
          borderRadius: "var(--ant-border-radius-base, 6px)",
        }}
        styles={{ body: { padding: 0 } }}
        onClick={handleCardClick}
        onKeyDown={handleCardKeyDown}
        tabIndex={isCardClickable ? 0 : undefined}
        role={isCardClickable ? "button" : undefined}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            gap: 0,
            alignItems: "stretch",
            width: "100%",
          }}
          {...(dragHandleProps && !isDefaultRound
            ? {
                ...dragHandleProps,
                style: {
                  cursor: "grab",
                  display: "flex",
                  alignItems: "stretch",
                  gap: 0,
                  width: "100%",
                  ...(dragHandleProps.style || {}),
                },
              }
            : {})}
        >
          {/* Main Content */}
          <div
            style={{
              flex: 1,
              padding: "6px 10px",
              display: "flex",
              flexDirection: "column",
              gap: 8,
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                gap: 8,
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              {/* Column 2.1: Drag Icon - Show for all rounds, but disabled for Profile Matching and existing rounds in edit mode */}
              <div
                className={`round-drag-handle ${
                  isDragHandleDisabled ? "round-drag-handle--disabled" : ""
                }`}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  pointerEvents: "none", // Icon is visual only, card handles drag
                  flexShrink: 0,
                  alignSelf: "stretch", // Stretch to match content column height
                  opacity: isDragHandleDisabled ? 0.3 : 1, // Visual feedback for disabled state
                }}
                title={
                  isProfileMatching
                    ? "Profile Matching is always Round 1"
                    : isDragDisabled
                    ? "Existing rounds cannot be reordered when editing a job post"
                    : "Drag to reorder"
                }
              >
                <HolderOutlined style={{ fontSize: 16 }} />
              </div>

              {/* Column 2.2: Main Content */}
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 8,
                  flex: 1,
                  minWidth: 0,
                }}
              >
                {/* Card Title Row: Title, Tags, and Actions */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 8,
                    marginBottom: 4,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      flex: 1,
                      minWidth: 0,
                    }}
                  >
                    <Typography.Text
                      strong
                      style={{ fontSize: "var(--ant-font-size-base)" }}
                    >
                      {roundTitle}
                    </Typography.Text>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 4, alignItems: "center" }}>
                      {/* Round Type Tag */}
                      {isProfileMatching || isOverallAssessment ? (
                        <Tag color="blue">Default</Tag>
                      ) : (
                        <>
                          <Tag color="purple">Custom</Tag>
                          {/* Question Type */}
                          {round.answerType && (
                            <Tag color={round.answerType === "simulation" ? "volcano" : "cyan"} style={{ margin: 0 }}>
                              {round.answerType === "mcq"
                                ? "Mcq"
                                : round.answerType === "coding"
                                ? "Programming"
                                : round.answerType === "descriptive"
                                ? "Descriptive"
                                : round.answerType === "mixed"
                                ? "Mixed"
                                : round.answerType === "simulation"
                                ? "Simulation"
                                : round.answerType === "multiple_choice"
                                ? "Multiple Choice"
                                : round.answerType === "code"
                                ? "Code"
                                : round.answerType === "text"
                                ? "Text"
                                : round.answerType.charAt(0).toUpperCase() +
                                  round.answerType.slice(1).toLowerCase()}
                            </Tag>
                          )}
                        </>
                      )}

                      {/* Eliminator and Pass Percentage - Not shown for Profile Matching */}
                      {!isProfileMatching && (
                        <>
                          {/* Separator */}
                          <span style={{ color: "var(--ant-color-text-quaternary)", margin: "0 4px" }}>|</span>

                          {/* Eliminator Status */}
                          <Tooltip title={round.isMustQualify 
                            ? "Eliminator: Candidates must qualify this round to proceed" 
                            : "Optional: Candidates can proceed regardless of performance"
                          }>
                            <Tag 
                              color={round.isMustQualify ? "error" : "success"} 
                              style={{ margin: 0, cursor: "help" }}
                            >
                              {round.isMustQualify ? "Eliminator" : "Optional"}
                            </Tag>
                          </Tooltip>

                          {/* Pass Percentage */}
                          <Tooltip title="Minimum pass percentage required to qualify this round">
                            <Tag style={{ margin: 0, cursor: "help" }} color="default">
                              Min Pass: {round.passPercentage ?? 70}%
                            </Tag>
                          </Tooltip>

                        </>
                      )}

                      {/* Profile Match Required toggle (job-level setting) */}
                      {isProfileMatching && (
                        <>
                          <span style={{ color: "var(--ant-color-text-quaternary)", margin: "0 4px" }}>|</span>
                          <Tooltip
                            title={
                              isProfileMatchRequiredDisabled
                                ? "Profile match requirement cannot be changed while editing a job post."
                                : "If enabled, candidates must pass Profile Matching to be considered qualified. If disabled, Profile Matching is still evaluated but won't eliminate candidates."
                            }
                          >
                            <span
                              style={{ display: "inline-flex", alignItems: "center", gap: 6, cursor: "help" }}
                              onMouseDown={handleInteractiveElementMouseDown}
                              onPointerDown={handleInteractiveElementMouseDown}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                Required
                              </Typography.Text>
                              <Switch
                                size="small"
                                checked={isProfileMatchRequired}
                                disabled={isProfileMatchRequiredDisabled}
                                onChange={(checked) => onProfileMatchRequiredChange?.(checked)}
                              />
                            </span>
                          </Tooltip>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Actions - aligned to the right */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 4,
                      flexShrink: 0,
                    }}
                  >
                    {/* Edit button - show for all rounds except Profile Matching in new/clone mode, only newly added rounds in edit mode */}
                    {!isProfileMatching &&
                      (!isEditMode || round._isNewlyAdded) && (
                        <Tooltip title="Edit round">
                          <Button
                            type="text"
                            size="small"
                            icon={<EditOutlined />}
                            onClick={(e) => {
                              e.stopPropagation();
                              onEdit(round);
                            }}
                            onMouseDown={handleInteractiveElementMouseDown}
                            onPointerDown={handleInteractiveElementMouseDown}
                            style={{
                              padding: "2px 6px",
                              height: 24,
                              pointerEvents: "auto",
                            }}
                          />
                        </Tooltip>
                      )}

                    {/* Delete button - show for custom rounds and Overall Assessment, and for newly added rounds in edit mode */}
                    {!isProfileMatching &&
                      (!isEditMode || round._isNewlyAdded) && (
                        <Popconfirm
                          title="Delete round?"
                          description="This action cannot be undone."
                          onConfirm={(e) => {
                            e?.stopPropagation();
                            handleDelete();
                          }}
                          okText="Delete"
                          cancelText="Cancel"
                          okButtonProps={{ danger: true }}
                        >
                          <Tooltip title="Delete round">
                            <Button
                              type="text"
                              size="small"
                              danger
                              icon={<DeleteOutlined />}
                              onClick={(e) => e.stopPropagation()}
                              onMouseDown={handleInteractiveElementMouseDown}
                              onPointerDown={handleInteractiveElementMouseDown}
                              style={{
                                padding: "2px 6px",
                                height: 24,
                                pointerEvents: "auto",
                              }}
                            />
                          </Tooltip>
                        </Popconfirm>
                      )}
                  </div>
                </div>

                {/* Question Generation Prompt (Primary Description) - Full Width */}
                <div style={{ width: "100%" }}>
                  {cardDescription ? (
                    <Typography.Paragraph
                      ellipsis={{ rows: 2, expandable: true }}
                      type="secondary"
                      style={{
                        fontSize: "var(--ant-font-size-sm)",
                        lineHeight: 1.5,
                        wordWrap: "break-word",
                        whiteSpace: "normal",
                        display: "block",
                        marginBottom: 0,
                      }}
                    >
                      {cardDescription}
                    </Typography.Paragraph>
                  ) : (
                    <Empty description="No description available" />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </Card>
    </>
  );
};

export default InterviewRoundCard;
